// ---------------------------------------------------------------------------
// Native (NO-AI) plain-English parser for the Tasks module.
//
// Two entry points:
//   • parseFilterText(text, ctx) → decode a filter (due scopes / tags / projects)
//   • parseTaskText(text, ctx)   → decode a new task (title / project / due /
//                                   tags / priority / recurrence / sub-tasks)
//
// ctx = { tags: (string | {tag})[], projects: {id,name}[] }
//
// Everything is done with keyword / substring matching + light date maths.
// No network calls, no LLM.
// ---------------------------------------------------------------------------

// ---- small local date helpers (user's LOCAL timezone) ---------------------
const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const endOfDay = (d) => { const x = new Date(d); x.setHours(23, 59, 59, 999); return x; };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const pad = (n) => String(n).padStart(2, '0');
const toYmd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const endOfDayIso = (d) => endOfDay(d).toISOString();
const isValid = (d) => d instanceof Date && !Number.isNaN(d.getTime());
const escapeRe = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

// ---- vocab ----------------------------------------------------------------
const WEEKDAYS = {
  sunday: 0, sun: 0, monday: 1, mon: 1, tuesday: 2, tue: 2, tues: 2,
  wednesday: 3, wed: 3, weds: 3, thursday: 4, thu: 4, thur: 4, thurs: 4,
  friday: 5, fri: 5, saturday: 6, sat: 6,
};
const WD_ALT = 'sunday|monday|tuesday|wednesday|thursday|friday|saturday|sun|mon|tues|tue|weds|wed|thurs|thur|thu|fri|sat';
const WD_NAME = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHNUM = {
  jan: 0, january: 0, feb: 1, february: 1, mar: 2, march: 2, apr: 3, april: 3,
  may: 4, jun: 5, june: 5, jul: 6, july: 6, aug: 7, august: 7, sep: 8, sept: 8,
  september: 8, oct: 9, october: 9, nov: 10, november: 10, dec: 11, december: 11,
};
const MONTHS_ALT = 'january|february|march|april|may|june|july|august|september|october|november|december|jan|feb|mar|apr|jun|jul|aug|sept|sep|oct|nov|dec';

const fmtDue = (d) => {
  const t0 = startOfDay(new Date());
  const diff = Math.round((startOfDay(d) - t0) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  if (diff === -1) return 'Yesterday';
  const opts = { day: 'numeric', month: 'short' };
  if (d.getFullYear() !== t0.getFullYear()) opts.year = 'numeric';
  return d.toLocaleDateString(undefined, opts);
};
const fmtTime = (d) => d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
const fmtDateTime = (d) => `${fmtDue(d)} ${fmtTime(d)}`;

const nextWeekday = (wd) => {
  const t0 = startOfDay(new Date());
  let diff = (wd - t0.getDay() + 7) % 7;
  if (diff === 0) diff = 7; // "friday" means the upcoming one, not today
  return addDays(t0, diff);
};

const monthDate = (day, mon, yr) => {
  const mi = MONTHNUM[String(mon).toLowerCase()];
  if (mi == null || !day || day < 1 || day > 31) return null;
  const t0 = startOfDay(new Date());
  let year = yr ? Number(yr) : t0.getFullYear();
  if (String(yr || '').length === 2) year += 2000;
  let d = new Date(year, mi, day, 0, 0, 0, 0);
  if (!yr && d < t0) d = new Date(year + 1, mi, day, 0, 0, 0, 0);
  return isValid(d) ? d : null;
};

const tagNames = (tags) => (tags || []).map((t) => (typeof t === 'string' ? t : t && t.tag)).filter(Boolean);

// ---------------------------------------------------------------------------
// FILTER parsing
// ---------------------------------------------------------------------------
export function parseFilterText(text, ctx = {}) {
  const projects = (ctx.projects || []).filter((p) => p && p.name);
  const vocab = tagNames(ctx.tags);
  const lower = ` ${String(text || '').toLowerCase().replace(/[_]+/g, ' ').replace(/\s+/g, ' ')} `;

  const scopes = new Set();
  const matched = [];

  if (/(overdue|over due|past due|late tasks?)/.test(lower)) { scopes.add('overdue'); matched.push('Overdue'); }
  if (/(no due date|no due|no date|undated|no deadline|without (a )?due|someday)/.test(lower)) { scopes.add('none'); matched.push('No date'); }
  if (/(next 7 days|next seven days|coming 7 days|within a week|in the next week)/.test(lower)) { scopes.add('next7'); matched.push('Next 7 days'); }
  if (/(due today|(^| )today( |$))/.test(lower)) { scopes.add('today'); matched.push('Today'); }

  // Range keywords collapse into a single custom range (union of bounds).
  const t0 = startOfDay(new Date());
  let rStart = null; let rEnd = null;
  const addRange = (s, e, label) => {
    if (!rStart || s < rStart) rStart = s;
    if (!rEnd || e > rEnd) rEnd = e;
    matched.push(label);
  };
  if (/(^| )tomorrow( |$)/.test(lower)) { const d = addDays(t0, 1); addRange(d, d, 'Tomorrow'); }
  if (/this week/.test(lower)) { const wIdx = (t0.getDay() + 6) % 7; addRange(t0, addDays(t0, 6 - wIdx), 'This week'); }
  if (/next week/.test(lower)) { const wIdx = (t0.getDay() + 6) % 7; const mon = addDays(t0, 7 - wIdx); addRange(mon, addDays(mon, 6), 'Next week'); }
  if (/this month/.test(lower)) { addRange(t0, new Date(t0.getFullYear(), t0.getMonth() + 1, 0), 'This month'); }
  if (/next month/.test(lower)) { const s = new Date(t0.getFullYear(), t0.getMonth() + 1, 1); addRange(s, new Date(t0.getFullYear(), t0.getMonth() + 2, 0), 'Next month'); }
  if (rStart && rEnd) scopes.add('custom');

  // Status — To-do / In progress / Done (completed). Order matters: match the
  // two-word "in progress" before the bare done/todo words.
  const statuses = [];
  const addStatus = (s, label) => { if (!statuses.includes(s)) { statuses.push(s); matched.push(label); } };
  if (/\b(in[\s-]?progress|ongoing|working on|in the works|wip|doing|started)\b/.test(lower)) addStatus('in_progress', 'In progress');
  if (/\b(to[\s-]?do|not started|not begun|pending|open tasks?|backlog)\b/.test(lower)) addStatus('todo', 'To do');
  if (/\b(done|complete|completed|finished|closed)\b/.test(lower)) addStatus('done', 'Done');

  // Priority — High / Medium / Normal.
  const priorities = [];
  const addPrio = (p, label) => { if (!priorities.includes(p)) { priorities.push(p); matched.push(label); } };
  if (/\b(high(?:est)? priority|urgent|asap|important|critical|top priority|p1)\b/.test(lower)) addPrio('high', 'High priority');
  if (/\b(medium priority|med priority|p2)\b/.test(lower)) addPrio('medium', 'Medium priority');
  if (/\b(low priority|normal priority|no rush|p3)\b/.test(lower)) addPrio('normal', 'Normal priority');

  // Tags — hashtags, then any known tag name that appears as a word.
  const foundTags = [];
  const pushTag = (name) => {
    const canon = vocab.find((v) => v.toLowerCase() === String(name).toLowerCase()) || name;
    if (!foundTags.some((x) => x.toLowerCase() === canon.toLowerCase())) foundTags.push(canon);
  };
  (String(text || '').match(/#([a-z0-9][\w-]*)/gi) || []).forEach((h) => pushTag(h.slice(1)));
  vocab.forEach((tg) => { if (new RegExp(`(^| )${escapeRe(tg.toLowerCase())}( |$)`).test(lower)) { pushTag(tg); matched.push(`#${tg}`); } });

  // Projects — match known project names (longest first).
  const foundProjects = [];
  [...projects].sort((a, b) => b.name.length - a.name.length).forEach((p) => {
    if (foundProjects.includes(p.id)) return;
    if (new RegExp(`(^| )${escapeRe(p.name.toLowerCase())}( |$)`).test(lower)) {
      foundProjects.push(p.id);
      matched.push(p.name);
    }
  });

  return {
    dueScopes: Array.from(scopes),
    dueStart: rStart ? toYmd(rStart) : '',
    dueEnd: rEnd ? toYmd(rEnd) : '',
    tags: foundTags,
    projectIds: foundProjects,
    statuses,
    priorities,
    matched,
    hasAny: scopes.size > 0 || foundTags.length > 0 || foundProjects.length > 0 || statuses.length > 0 || priorities.length > 0,
  };
}

// ---------------------------------------------------------------------------
// DUE-DATE extraction for a single task. Returns { date, str } | null.
// The most specific / explicit patterns are tried first.
// ---------------------------------------------------------------------------
function extractDate(s) {
  const t0 = startOfDay(new Date());
  let m;
  // ISO yyyy-mm-dd
  if ((m = s.match(/\b(\d{4})-(\d{1,2})-(\d{1,2})\b/))) {
    const d = new Date(+m[1], +m[2] - 1, +m[3]);
    if (isValid(d)) return { date: d, str: m[0] };
  }
  // dd/mm(/yy) — day-first (India). Swap when clearly month-first.
  if ((m = s.match(/\b(\d{1,2})[/\-.](\d{1,2})(?:[/\-.](\d{2,4}))?\b/))) {
    let dd = +m[1]; let mm = +m[2]; let yy = m[3] ? +m[3] : t0.getFullYear();
    if (yy < 100) yy += 2000;
    if (mm > 12 && dd <= 12) { const t = dd; dd = mm; mm = t; }
    const d = new Date(yy, mm - 1, dd);
    if (isValid(d) && mm >= 1 && mm <= 12 && dd >= 1 && dd <= 31) return { date: d, str: m[0] };
  }
  // "25th december 2026" / "25 dec"
  if ((m = s.match(new RegExp(`\\b(\\d{1,2})(?:st|nd|rd|th)?\\s+(?:of\\s+)?(${MONTHS_ALT})(?:\\s+(\\d{2,4}))?\\b`, 'i')))) {
    const d = monthDate(+m[1], m[2], m[3]);
    if (d) return { date: d, str: m[0] };
  }
  // "december 25th 2026" / "dec 25"
  if ((m = s.match(new RegExp(`\\b(${MONTHS_ALT})\\s+(\\d{1,2})(?:st|nd|rd|th)?(?:,?\\s+(\\d{2,4}))?\\b`, 'i')))) {
    const d = monthDate(+m[2], m[1], m[3]);
    if (d) return { date: d, str: m[0] };
  }
  // relative words
  if ((m = s.match(/\bday after tomorrow\b/i))) return { date: addDays(t0, 2), str: m[0] };
  if ((m = s.match(/\b(tomorrow|tmrw|tmr)\b/i))) return { date: addDays(t0, 1), str: m[0] };
  if ((m = s.match(/\b(today|tonight|end of (the )?day|eod)\b/i))) return { date: t0, str: m[0] };
  if ((m = s.match(/\bin\s+(\d+)\s+days?\b/i))) return { date: addDays(t0, +m[1]), str: m[0] };
  if ((m = s.match(/\bin\s+(\d+)\s+weeks?\b/i))) return { date: addDays(t0, +m[1] * 7), str: m[0] };
  if ((m = s.match(/\bin\s+(\d+)\s+months?\b/i))) { const d = new Date(t0); d.setMonth(d.getMonth() + +m[1]); return { date: d, str: m[0] }; }
  if ((m = s.match(/\bnext week\b/i))) { const wIdx = (t0.getDay() + 6) % 7; return { date: addDays(t0, 7 - wIdx), str: m[0] }; }
  if ((m = s.match(/\b(this weekend|weekend)\b/i))) { const toSat = ((6 - t0.getDay() + 7) % 7) || 6; return { date: addDays(t0, toSat), str: m[0] }; }
  if ((m = s.match(/\b(end of (the )?month|month end)\b/i))) return { date: new Date(t0.getFullYear(), t0.getMonth() + 1, 0), str: m[0] };
  if ((m = s.match(/\bnext month\b/i))) { const d = new Date(t0); d.setMonth(d.getMonth() + 1); return { date: d, str: m[0] }; }
  if ((m = s.match(/\bthis month\b/i))) return { date: new Date(t0.getFullYear(), t0.getMonth() + 1, 0), str: m[0] };
  // weekday, optionally prefixed by on/by/this/coming/next
  if ((m = s.match(new RegExp(`\\b(?:on |by |this |coming |next )?(${WD_ALT})\\b`, 'i')))) {
    const wd = WEEKDAYS[m[1].toLowerCase()];
    if (wd != null) return { date: nextWeekday(wd), str: m[0] };
  }
  return null;
}

// ---------------------------------------------------------------------------
// TIME-OF-DAY extraction → { h, min, str } | null. Powers reminders such as
// "remind me … at 4 pm". Requires am/pm, a colon, or an explicit "at"/"@" so a
// bare number inside a title isn't mistaken for a time.
// ---------------------------------------------------------------------------
function extractTime(s) {
  let m;
  if ((m = s.match(/\b(noon|midday)\b/i))) return { h: 12, min: 0, str: m[0] };
  if ((m = s.match(/\bmidnight\b/i))) return { h: 0, min: 0, str: m[0] };
  // "(at) 4 pm", "4:30pm", "at 4:30 p.m."
  if ((m = s.match(/(?:\bat\s+|@\s*)?\b(\d{1,2})(?::(\d{2}))?\s*(a\.?m\.?|p\.?m\.?)\b/i))) {
    let h = +m[1]; const min = m[2] ? +m[2] : 0;
    const mer = m[3].toLowerCase().replace(/\./g, '');
    if (h >= 1 && h <= 12 && min <= 59) {
      if (mer === 'pm' && h < 12) h += 12;
      if (mer === 'am' && h === 12) h = 0;
      return { h, min, str: m[0].trim() };
    }
  }
  // 24-hour "(at) 16:00"
  if ((m = s.match(/(?:\bat\s+|@\s*)?\b(\d{1,2}):(\d{2})\b/))) {
    const h = +m[1]; const min = +m[2];
    if (h <= 23 && min <= 59) return { h, min, str: m[0].trim() };
  }
  // "at 4", "at 4 o'clock" (no am/pm) — 1–7 ⇒ afternoon, 8–11 ⇒ morning, 12 ⇒ noon.
  if ((m = s.match(/\bat\s+(\d{1,2})(?:\s*o'?clock)?\b/i))) {
    let h = +m[1];
    if (h >= 0 && h <= 23) {
      if (h >= 1 && h <= 7) h += 12;
      return { h, min: 0, str: m[0].trim() };
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// LEAD TIME extraction → { mins, label, str } | null. Powers "remind me 30
// minutes before" so the ping lands ahead of the event/due time. Requires a
// trailing before/ahead/prior/etc so it can't swallow a plain due time.
// ---------------------------------------------------------------------------
function extractLead(s) {
  const m = s.match(/\b(?:remind\s+me\s+)?(?:(\d+)\s*(min(?:ute)?s?|hours?|hrs?|days?|weeks?)|(a|an|one)\s+(minute|min|hour|hr|day|week))\s+(?:before(?:hand)?|ahead|prior|earlier|in\s+advance|early)\b/i);
  if (!m) return null;
  let qty; let unit;
  if (m[1]) { qty = parseInt(m[1], 10); unit = m[2].toLowerCase(); }
  else { qty = 1; unit = m[4].toLowerCase(); }
  const per = unit.startsWith('min') ? 1 : unit.startsWith('h') ? 60 : unit.startsWith('d') ? 1440 : unit.startsWith('w') ? 10080 : 1;
  if (!qty || qty <= 0) return null;
  const mins = qty * per;
  const uName = per === 1 ? 'min' : per === 60 ? 'hour' : per === 1440 ? 'day' : 'week';
  const label = `${qty} ${uName}${qty > 1 ? 's' : ''}`;
  return { mins, label, str: m[0].trim() };
}

// ---------------------------------------------------------------------------
// TASK parsing
// ---------------------------------------------------------------------------
export function parseTaskText(text, ctx = {}) {
  const projects = (ctx.projects || []).filter((p) => p && p.name);
  const vocab = tagNames(ctx.tags);
  let work = ` ${String(text || '').replace(/\s+/g, ' ').trim()} `;

  const result = {
    title: '', projectId: null, projectName: null,
    dueAt: null, dueLabel: '', tags: [], priority: 'normal', priorityLabel: '',
    recurrence: 'none', recurrenceLabel: '', recurrenceWeekdays: [], subtasks: [],
    reminderAt: null, reminderLabel: '', reminderRule: 'none',
    shareUserIds: [], shareNames: [],
    shareGroupIds: [], shareGroupNames: [],
  };

  const stripStr = (str) => { if (str) work = work.split(str).join(' '); };
  const stripRe = (re) => { work = work.replace(re, ' '); };

  // 0) People to share with — "remind/ask/tell/get/have X & Y to <action>", or
  //    an explicit "shared with / assign to X & Y". Names are matched against
  //    the user directory (ctx.users); "me/us" are ignored (that's the owner).
  const dirUsers = (ctx.users || []).filter((u) => u && (u.name || u.email));
  const matchUser = (raw) => {
    const tk = String(raw || '').trim().toLowerCase().replace(/[^a-z0-9@.\s'-]/g, '').trim();
    if (!tk || ['me', 'us', 'myself', 'everyone', 'team', 'all', 'him', 'her', 'them'].includes(tk)) return null;
    const nm = (u) => String(u.name || '').toLowerCase();
    const first = (u) => nm(u).split(/\s+/)[0];
    const local = (u) => String(u.email || '').toLowerCase().split('@')[0];
    // Try the whole token (full "First Last"), then just its first word.
    for (const c of [tk, tk.split(/\s+/)[0]]) {
      const u = dirUsers.find((x) => nm(x) === c) || dirUsers.find((x) => first(x) === c) || dirUsers.find((x) => local(x) === c);
      if (u) return u;
    }
    const f0 = tk.split(/\s+/)[0];
    if (f0.length >= 3) { const u = dirUsers.find((x) => first(x).startsWith(f0)); if (u) return u; }
    return null;
  };
  // Groups to share with — matched against ctx.groups (owned groups only, since
  // only a group's owner can share a task with it). Multi-word names supported.
  const dirGroups = (ctx.groups || []).filter((g) => g && g.name);
  const matchGroup = (raw) => {
    const tk = String(raw || '').trim().toLowerCase().replace(/[^a-z0-9@.\s'-]/g, '').trim();
    if (!tk) return null;
    const gn = (g) => String(g.name || '').toLowerCase();
    let g = dirGroups.find((x) => gn(x) === tk);
    if (g) return g;
    if (tk.length >= 3) { g = dirGroups.find((x) => gn(x).startsWith(tk)); if (g) return g; }
    return null;
  };
  const addNames = (namesStr) => {
    String(namesStr || '').split(/\s*(?:,|&|\+|\/|\band\b)\s*/i).forEach((tok) => {
      const u = matchUser(tok);
      if (u) { if (!result.shareUserIds.includes(u.id)) { result.shareUserIds.push(u.id); result.shareNames.push(u.name || u.email); } return; }
      const g = matchGroup(tok);
      if (g && !result.shareGroupIds.includes(g.id)) { result.shareGroupIds.push(g.id); result.shareGroupNames.push(g.name); }
    });
  };
  const peopleM = work.match(/\b(remind|ask|tell|get|have|notify|nudge)\s+(.+?)\s+to\s+/i);
  if (peopleM) {
    const before = result.shareUserIds.length + result.shareGroupIds.length;
    addNames(peopleM[2]);
    const matched = (result.shareUserIds.length + result.shareGroupIds.length) > before;
    // Only rewrite the title when we actually recognised a collaborator in the
    // directory. An unknown name (e.g. "Remind John to …" where John isn't a
    // Notes user) is left untouched so it stays in the title as ordinary words.
    if (matched) {
      const verb = peopleM[1].toLowerCase();
      if (verb === 'remind') {
        // "Remind <name> to X" reads naturally as "Reminder to X" once the
        // collaborator's name is lifted out into the share list.
        work = work.replace(peopleM[0], ' Reminder to ');
      } else {
        // ask / tell / get / have / notify / nudge — drop the delegation so the
        // action itself becomes the title ("Ask <name> to send report" → "Send
        // report").
        stripStr(peopleM[0]);
      }
      // Drop a delegated indirect object: "give me the list" → "give the list".
      work = work.replace(/\b(give|send|get|share|forward|email|bring|provide|hand|show)\s+(?:me|us)\b/i, '$1');
    }
  }
  const NW = "(?!(?:today|tomorrow|tonight|tonite|next|this|on|by|at|due|every|each|the|a|an|and|with|to|please|about)\\b)[A-Za-z][\\w'’.-]*";
  const nameList = `${NW}(?:\\s+${NW})?(?:\\s*(?:,|&|\\+|\\band\\b)\\s*${NW}(?:\\s+${NW})?){0,5}`;
  const withM = work.match(new RegExp(`\\b(?:shared?\\s+with|assign(?:ed)?\\s+to|share\\s+to)\\s+(${nameList})`, 'i'));
  if (withM) {
    const before = result.shareUserIds.length + result.shareGroupIds.length;
    addNames(withM[1]);
    if (result.shareUserIds.length + result.shareGroupIds.length > before) {
      stripStr(withM[0]);
      work = work.replace(/\s+(?:and|with|,)\s*$/i, ' '); // tidy a dangling connector
    }
  }

  // 0b) Explicit @mentions — "@Name" / "@First Last" shares with that teammate
  //     or group (mirrors the Smart-Add "@" autocomplete). Each recognised
  //     mention is lifted out of the title. Guarded so it never fires inside an
  //     email ("a@b.com") — the "@" must start the string or follow a space.
  const atRe = /(?:^|\s)@([A-Za-z][\w'’.-]*)(?:\s+([A-Za-z][\w'’.-]*))?/g;
  const atStrip = [];
  let atM;
  while ((atM = atRe.exec(work)) !== null) {
    const oneWord = atM[1];
    const twoWord = atM[2] ? `${atM[1]} ${atM[2]}` : null;
    let hit = null; let usedTwo = false;
    if (twoWord) {
      const u2 = matchUser(twoWord);
      if (u2 && String(u2.name || '').toLowerCase() === twoWord.toLowerCase()) { hit = { u: u2 }; usedTwo = true; }
      else {
        const g2 = matchGroup(twoWord);
        if (g2 && String(g2.name || '').toLowerCase() === twoWord.toLowerCase()) { hit = { g: g2 }; usedTwo = true; }
      }
    }
    if (!hit) {
      const u1 = matchUser(oneWord);
      if (u1) hit = { u: u1 };
      else { const g1 = matchGroup(oneWord); if (g1) hit = { g: g1 }; }
    }
    if (hit) {
      if (hit.u) { if (!result.shareUserIds.includes(hit.u.id)) { result.shareUserIds.push(hit.u.id); result.shareNames.push(hit.u.name || hit.u.email); } }
      else if (hit.g) { if (!result.shareGroupIds.includes(hit.g.id)) { result.shareGroupIds.push(hit.g.id); result.shareGroupNames.push(hit.g.name); } }
      atStrip.push(usedTwo ? `@${atM[1]} ${atM[2]}` : `@${atM[1]}`);
    }
  }
  atStrip.forEach((s) => stripStr(s));

  // 1) Sub-tasks — everything after a "sub-tasks / steps / checklist" marker.
  const subMatch = work.match(/\b(?:with\s+|and\s+)?(?:sub[\s-]?tasks?|sub[\s-]?items?|steps|checklist|to[\s-]?dos?)\s*(?:like|such as|:|-|=|are)?\s+(.+?)\s*$/i);
  if (subMatch && subMatch[1]) {
    const parts = subMatch[1]
      .split(/\s*(?:,|;|\/|·|•|\n|\band\b|\bthen\b)\s*/i)
      .map((p) => p.replace(/^[\s.\-]+|[\s.\-]+$/g, '').trim())
      .filter(Boolean)
      .map((p) => cap(p));
    if (parts.length) {
      result.subtasks = parts.slice(0, 25);
      stripStr(subMatch[0]);
    }
  }

  // 2) Recurrence — "every Mon & Thu" → weekdays rule; single day → weekly.
  let everyWeekday = null;      // JS Sun=0 convention (for due-date calc)
  let everyWeekdaysJs = null;   // multiple weekdays (JS Sun=0) for due-date calc
  // "every weekday" (Mon–Fri) / "every weekend" (Sat–Sun) ranges.
  const rangeM = work.match(/\b(?:every|each|on)\s+(weekday|weekend)s?\b/i);
  if (rangeM) {
    const isWeekend = /weekend/i.test(rangeM[1]);
    result.recurrence = 'weekdays';
    result.recurrenceWeekdays = isWeekend ? [5, 6] : [0, 1, 2, 3, 4]; // Mon=0
    everyWeekdaysJs = isWeekend ? [6, 0] : [1, 2, 3, 4, 5];           // JS Sun=0
    result.recurrenceLabel = isWeekend ? 'On weekends' : 'On weekdays';
    stripStr(rangeM[0]);
  }
  // "twice a day / twice daily" → a 12-hourly reminder cadence (applied in 4b).
  // Detect + strip BEFORE the daily matcher so "daily" isn't consumed alone.
  let twiceDaily = false;
  const twiceM = work.match(/\b(twice\s+(?:a|per)\s+day|twice\s+daily|2x\s*(?:a\s*)?day|two\s+times\s+a\s+day)\b/i);
  if (twiceM) { twiceDaily = true; stripStr(twiceM[0]); }
  const WD_ONE = `(?:${WD_ALT})`;
  const WD_SEP = '(?:\\s*,\\s*|\\s*&\\s*|\\s+and\\s+|\\s+)';
  const everyRe = new RegExp(`\\bevery\\s+(${WD_ONE}(?:${WD_SEP}${WD_ONE})*)\\b`, 'i');
  const ewd = result.recurrence === 'none' ? work.match(everyRe) : null;
  if (ewd) {
    const tokens = (ewd[1].match(new RegExp(WD_ONE, 'ig')) || [])
      .map((t) => WEEKDAYS[t.toLowerCase()]).filter((n) => n != null);
    const uniqJs = [...new Set(tokens)];
    const MON0 = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    if (uniqJs.length >= 2) {
      result.recurrence = 'weekdays';
      // Convert JS Sun=0 → Mon=0 for backend/UI parity.
      result.recurrenceWeekdays = [...new Set(uniqJs.map((js) => (js + 6) % 7))].sort((a, b) => a - b);
      result.recurrenceLabel = `On ${result.recurrenceWeekdays.map((d) => MON0[d]).join(', ')}`;
      everyWeekdaysJs = uniqJs;
      stripStr(ewd[0]);
    } else if (uniqJs.length === 1) {
      result.recurrence = 'weekly';
      everyWeekday = uniqJs[0];
      result.recurrenceLabel = `Weekly on ${WD_NAME[everyWeekday] || ''}`;
      stripStr(ewd[0]);
    }
  }
  if (result.recurrence === 'none') {
    const recPats = [
      [/\b(every\s+day|daily|each\s+day)\b/i, 'daily', 'Daily'],
      [/\b(every\s+week|weekly|each\s+week)\b/i, 'weekly', 'Weekly'],
      [/\b(every\s+month|monthly|each\s+month)\b/i, 'monthly', 'Monthly'],
      [/\b(every\s+year|yearly|annually|annual)\b/i, 'annually', 'Yearly'],
    ];
    for (const [re, val, label] of recPats) {
      const mm = work.match(re);
      if (mm) { result.recurrence = val; result.recurrenceLabel = label; stripStr(mm[0]); break; }
    }
  }

  // 3) Priority
  if (/(!!+|\b(high(?:est)? priority|urgent|asap|important|critical|top priority|priority\s*[:=]?\s*high|p1)\b)/i.test(work)) {
    result.priority = 'high'; result.priorityLabel = 'High';
    stripRe(/\b(high(?:est)? priority|urgent|asap|important|critical|top priority|priority\s*[:=]?\s*high|p1)\b/ig);
    stripRe(/!!+/g);
  } else if (/\b(medium priority|med priority|priority\s*[:=]?\s*medium|p2)\b/i.test(work)) {
    result.priority = 'medium'; result.priorityLabel = 'Medium';
    stripRe(/\b(medium priority|med priority|priority\s*[:=]?\s*medium|p2)\b/ig);
  } else if (/\b(low priority|no rush|whenever|priority\s*[:=]?\s*low|p3)\b/i.test(work)) {
    result.priority = 'normal'; result.priorityLabel = 'Low';
    stripRe(/\b(low priority|no rush|whenever|priority\s*[:=]?\s*low|p3)\b/ig);
  }

  // 4) Due date (drop a leading due/on/by preposition together with the phrase)
  let dueDate = null;
  const due = extractDate(work);
  if (due) {
    dueDate = due.date;
    result.dueAt = endOfDayIso(due.date);
    result.dueLabel = fmtDue(due.date);
    const pre = new RegExp(`\\b(due(?:\\s+on|\\s+by)?|on|by|before)\\s+${escapeRe(due.str)}`, 'i');
    if (pre.test(work)) stripRe(pre); else stripStr(due.str);
  } else if (everyWeekday != null) {
    dueDate = nextWeekday(everyWeekday);
    result.dueAt = endOfDayIso(dueDate);
    result.dueLabel = fmtDue(dueDate);
  } else if (everyWeekdaysJs && everyWeekdaysJs.length) {
    const dates = everyWeekdaysJs.map((w) => nextWeekday(w));
    dueDate = dates.reduce((a, b) => (b < a ? b : a));
    result.dueAt = endOfDayIso(dueDate);
    result.dueLabel = fmtDue(dueDate);
  }

  // 4b) Reminders — a time-of-day, an "N before" lead, and/or "twice a day".
  //   "Remind me … today at 4 pm"            ⇒ due today + reminder 4 PM.
  //   "… at 5 pm, remind me 30 minutes before" ⇒ reminder 4:30 PM.
  //   "take meds twice a day at 9 am"          ⇒ reminder 9 AM, repeats every 12h.
  const lead = extractLead(work);
  const tod = extractTime(work);
  if (tod || lead || twiceDaily) {
    const base = dueDate ? new Date(dueDate) : startOfDay(new Date());
    // Anchor: explicit time-of-day; else the next upcoming 9 AM/9 PM for a bare
    // "twice a day"; else the due instant (end of day) so an "N before" lead
    // still has something to bite.
    let anchor = new Date(base);
    if (tod) {
      anchor.setHours(tod.h, tod.min, 0, 0);
    } else if (twiceDaily) {
      const now = Date.now();
      const c9 = new Date(base); c9.setHours(9, 0, 0, 0);
      const c21 = new Date(base); c21.setHours(21, 0, 0, 0);
      if (c9.getTime() > now) anchor = c9;
      else if (c21.getTime() > now) anchor = c21;
      else { anchor = new Date(base); anchor.setDate(anchor.getDate() + 1); anchor.setHours(9, 0, 0, 0); }
    } else if (result.dueAt) {
      anchor = new Date(result.dueAt);
    }
    const remind = new Date(anchor.getTime() - (lead ? lead.mins * 60000 : 0));
    result.reminderAt = remind.toISOString();
    result.reminderLabel = fmtDateTime(remind) + (lead ? ` (${lead.label} before)` : '');
    // A bare time/lead with no explicit date means "today".
    if (!result.dueAt) { result.dueAt = endOfDayIso(base); result.dueLabel = fmtDue(base); }
    // Reminder cadence: an explicit "twice a day" wins over a task recurrence.
    if (twiceDaily) result.reminderRule = 'twicedaily';
    else if (['daily', 'weekly', 'monthly', 'annually'].includes(result.recurrence)) result.reminderRule = result.recurrence;
    if (tod) stripStr(tod.str);
    if (lead) stripStr(lead.str);
  }

  // 5) Project — match a known project name (longest first), remove a leading
  //    "for / in / project" preposition with it.
  const projSorted = [...projects].sort((a, b) => b.name.length - a.name.length);
  for (const p of projSorted) {
    const nm = escapeRe(p.name);
    if (new RegExp(`\\b${nm}\\b`, 'i').test(work)) {
      result.projectId = p.id; result.projectName = p.name;
      stripRe(new RegExp(`\\b(?:for|in|under|inside|within|to|on|project)\\s+(?:the\\s+)?${nm}(?:\\s+project)?\\b`, 'i'));
      stripRe(new RegExp(`\\b${nm}(?:\\s+project)?\\b`, 'i'));
      break;
    }
  }

  // 6) Tags — hashtags, "tag(ged) X", then any known tag word.
  const pushTag = (name) => {
    const canon = vocab.find((v) => v.toLowerCase() === String(name).toLowerCase()) || name;
    if (canon && !result.tags.some((x) => x.toLowerCase() === canon.toLowerCase())) result.tags.push(canon);
  };
  (work.match(/#([a-z0-9][\w-]*)/gi) || []).forEach((h) => pushTag(h.slice(1)));
  stripRe(/#[a-z0-9][\w-]*/gi);
  let tm; const tagWordRe = /\b(?:tagged|tag|label|labelled|labeled)\s+(?:as\s+|with\s+)?([a-z0-9][\w-]*)\b/ig;
  while ((tm = tagWordRe.exec(work)) !== null) pushTag(tm[1]);
  stripRe(/\b(?:tagged|tag|label|labelled|labeled)\s+(?:as\s+|with\s+)?[a-z0-9][\w-]*\b/ig);
  // Bare words that happen to match an existing tag name are added as tags BUT
  // kept in the title — they are part of the natural sentence. e.g. typing
  // "To check GST refund received today" tags #GST while the title still reads
  // "Check GST refund received" (only explicit #hashtags / "tagged X" forms above
  // are stripped from the title).
  vocab.forEach((tg) => {
    const re = new RegExp(`\\b${escapeRe(tg)}\\b`, 'i');
    if (re.test(work)) { pushTag(tg); }
  });

  // 7) Title — whatever is left, cleaned of command verbs & dangling glue words.
  let title = work.replace(/\s+/g, ' ').trim();
  title = title.replace(/^(?:please\s+|can you\s+|could you\s+)?(?:add|create|make|new|set\s?up|schedule|remind me to|remember to|note to|task\s*[:\-]?|to)\s+/i, '').trim();
  title = title.replace(/^(?:a\s+|the\s+)?task\s+(?:to|for|:)\s*/i, '').trim();
  title = title.replace(/\b(?:for|in|on|by|with|and|to|the|due|at|of|a)\s*$/i, '').trim();
  title = title.replace(/^(?:for|in|on|by|with|and|to|due|at|of)\b\s*/i, '').trim();
  title = title.replace(/\s{2,}/g, ' ').replace(/^[\s,;:\-]+|[\s,;:\-]+$/g, '').trim();
  result.title = title ? cap(title) : 'Untitled task';

  return result;
}

// ---- example prompts for placeholders / hints -----------------------------
export const FILTER_EXAMPLES = [
  'in progress tasks due today',
  'to-do high priority for Marketing',
  'overdue tasks tagged payments',
];
export const TASK_EXAMPLES = [
  'Pay electricity bill tomorrow high priority tagged bills',
  'Prepare Q4 report for Finance due next friday with subtasks gather data, draft, review',
  'Standup notes every monday for Team',
];
